<script setup lang="ts">
import { defineAsyncComponent } from "vue";
import { useDisplay } from "vuetify";
import BarcodeScannerDialog from "@/components/shared/BarcodeScannerDialog.vue";
import { parseScannedCode, scannedCodeRoute } from "@/constants/barcode";

/**
 * The panels are loaded lazily and NOT statically imported.
 *
 * This component is mounted in `App.vue`, and the panels pull in the API layer,
 * which reaches `httpClient` — which itself imports the router, which imports
 * the pages. A static import here closes that cycle and crashes at boot with
 * "Cannot access 'HttpClient' before initialization". Keep these async.
 */
const QrPetPanel = defineAsyncComponent(() => import("@/components/qr-search/QrPetPanel.vue"));
const QrRoomPanel = defineAsyncComponent(() => import("@/components/qr-search/QrRoomPanel.vue"));
import useQrScannerStore from "@/stores/qrScanner.store";
import useToastify from "@/stores/toastify.store";
import { overlayTargetEl } from "@/utils/overlayTarget";

/**
 * App-wide QR scanner + result modal. Mounted ONCE in `App.vue` so scanning
 * works from any page without navigating away — staff scan mid-task and must
 * keep the page they were on.
 *
 * On plain HTTP `getUserMedia` is blocked, so the camera dialog degrades to its
 * "needs HTTPS" state; the manual box inside this overlay is the working path
 * there (and the full `/qr-search` page remains available as a deep-link target).
 */
const store = useQrScannerStore();
const { t } = useI18n({ useScope: "global" });
const { smAndDown } = useDisplay();
const toast = useToastify();

const manualCode = ref("");

const scannerModel = computed({
  get: () => store.scannerOpen,
  set: (value: boolean) => { store.scannerOpen = value; },
});

const resultModel = computed({
  get: () => store.hasResult,
  set: (value: boolean) => { if (!value) store.closeResult(); },
});

const router = useRouter();

/** Shared by the camera and the manual box so both behave identically. */
function resolveText(text: string) {
  const parsed = parseScannedCode(text);

  if (!parsed) {
    toast.error({ text: t("alkokh.qrSearch.unrecognisedCode") });

    return false;
  }

  /**
   * ⚠️ HANDLED BEFORE `store.show`, DELIBERATELY. The result modal below picks its icon, its
   * title and its panel with `kind === 'pet' ? … : …` ternaries, so anything that is not a pet
   * falls through to the ROOM panel — a boarding code reaching it would be titled "Room" and
   * looked up as a room id. Routing here keeps those three ternaries a true binary and leaves
   * the existing panels untouched.
   *
   * A cage sticker also deserves the record itself rather than a summary card: the detail route
   * is already keyed on the `Pet Boarding` name, so this needs no panel of its own.
   */
  // Boarding → the booking; driver → his settlement (with the settle dialog open).
  const target = scannedCodeRoute(parsed);

  if (target) {
    store.closeAll();
    void router.push(target);

    return true;
  }

  store.show(parsed);

  return true;
}

function onDecoded(text: string) {
  // The scanner closes itself on any successful decode, so an unrecognised code
  // would otherwise leave nothing on screen but a toast. Reopen it to retry.
  if (!resolveText(text))
    nextTick(() => { store.scannerOpen = true; });
}

function submitManual() {
  const value = manualCode.value.trim();
  if (!value)
    return;

  if (resolveText(value))
    manualCode.value = "";
}

/** Clear the typed code whenever the camera is reopened. */
watch(() => store.scannerOpen, (open) => {
  if (open)
    manualCode.value = "";
});
</script>

<template>
  <BarcodeScannerDialog
    v-model="scannerModel"
    :attach="overlayTargetEl"
    :title="t('alkokh.qrSearch.title')"
    :hint="t('alkokh.qrSearch.scanHint')"
    @decoded="onDecoded"
  />

  <VDialog
    v-model="resultModel"
    :attach="overlayTargetEl"
    max-width="560"
    :fullscreen="smAndDown"
    scrollable
  >
    <VCard
      v-if="store.result"
      rounded="lg"
    >
      <VCardItem>
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon
            :icon="store.result.kind === 'pet' ? 'tabler-paw' : 'tabler-door'"
            size="20"
          />
          {{ store.result.kind === "pet"
            ? t("alkokh.qrSearch.pet.title")
            : store.result.kind === "boarding"
              ? t("alkokh.qrSearch.boarding.title")
              : t("alkokh.qrSearch.room.title") }}
        </VCardTitle>

        <template #append>
          <VBtn
            icon="tabler-x"
            variant="text"
            size="small"
            color="secondary"
            :aria-label="t('alkokh.global.close')"
            @click="store.closeResult()"
          />
        </template>
      </VCardItem>

      <VDivider />

      <VCardText class="py-5">
        <!-- Navigating from a panel must dismiss it, or the modal would hang
             over the page the user just jumped to. -->
        <QrPetPanel
          v-if="store.result.kind === 'pet'"
          :key="store.result.id"
          :pet-id="store.result.id"
          @navigate="store.closeAll()"
        />
        <!-- Explicitly `room`, not `v-else`: a third kind must never fall through to a panel
             that would look its id up as a room. `boarding` never reaches here — see
             `resolveText` — and this makes that guarantee structural rather than incidental. -->
        <QrRoomPanel
          v-else-if="store.result.kind === 'room'"
          :key="store.result.id"
          :room-id="store.result.id"
          @navigate="store.closeAll()"
        />
      </VCardText>

      <VDivider />

      <VCardActions class="px-4 py-3">
        <VBtn
          variant="text"
          prepend-icon="tabler-scan"
          @click="store.open()"
        >
          {{ t("alkokh.qrSearch.scanAnother") }}
        </VBtn>
        <VSpacer />
        <VBtn
          variant="tonal"
          @click="store.closeResult()"
        >
          {{ t("alkokh.global.close") }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
